import { BrandclubEventType, TrackingPixel } from "./types";

export const timeoutPromise = <T>(ms: number, name: string, fn: () => Promise<T>) => {
  return new Promise<T>((resolve, reject) => {
    setTimeout(() => {
      reject(new Error(`${name} timed out after ${ms}ms`));
    }, ms);
    fn().then(resolve, reject);
  });
};

function mapAppEventToExternalCustomEvent(eventType: string, supportedEventsConfig: TrackingPixel['eventMapping']) {
  if (!supportedEventsConfig) {
    return undefined;
  }

  // Page View events
  if (supportedEventsConfig.pageView?.enabled &&
    (eventType?.endsWith('page view') || eventType?.endsWith('bc_checkout_page_view'))) {
    return {
      eventName: supportedEventsConfig.pageView.mappedEventName,
      eventType: BrandclubEventType.PageView,
    };
  }

  // Navigate to Retailer events
  if (supportedEventsConfig.navigateToRetailer?.enabled &&
    eventType?.endsWith('openRetailerProductPage')) {
    return {
      eventName: supportedEventsConfig.navigateToRetailer.mappedEventName,
      eventType: BrandclubEventType.NavigateToRetailer,
    };
  }

  // Add to Cart events
  if (supportedEventsConfig.addToCart?.enabled &&
    eventType?.endsWith(':addToCart')) {
    return {
      eventName: supportedEventsConfig.addToCart.mappedEventName,
      eventType: BrandclubEventType.AddToCart,
    };
  }

  // Order Submitted events
  if (supportedEventsConfig.orderSubmitted?.enabled &&
    eventType?.endsWith('bc_checkout_order_submit')) {
    return {
      eventName: supportedEventsConfig.orderSubmitted.mappedEventName,
      eventType: BrandclubEventType.OrderSubmitted,
    };
  }

  return undefined;
}

export function parseEventAndPayload(eventName: string, payload: any, supportedEventsConfig: TrackingPixel['eventMapping']) {
  const mappedEvent = mapAppEventToExternalCustomEvent(eventName, supportedEventsConfig);

  if (!mappedEvent?.eventName || !mappedEvent?.eventType) {
    return {
      eventName: undefined,
      eventPayload: {},
    } as const;
  }

  const { eventName: externalEventName, eventType } = mappedEvent;
  const convertedPayload = JSON.parse(
    JSON.stringify({
      stacklineSku: payload?.sku || payload?.stacklineSku,
      retailerId: payload?.retailerId,
      retailerSku: payload?.retailerSku,
      retailPrice: payload?.retailPrice,
      pageId: payload?.pageId,
      pageType: payload?.pageType,
      brandId: payload?.brandId,
      brandName: payload?.brandName,
      categoryId: payload?.categoryId,
      categoryName: payload?.categoryName,
      subCategoryId: payload?.subCategoryId,
      subCategoryName: payload?.subCategoryName,
      jl_id: payload?.jl_id,
      jl_c: payload?.jl_c,
      jl_m: payload?.jl_m,
      jl_s: payload?.jl_s,
      checkoutItems: payload?.checkoutItems?.map((item: any) => ({
        retailerId: item.retailerId,
        retailerSku: item.retailerSku,
        stacklineSku: item.stacklineSku,
        quantity: item.quantity,
      })),
      checkoutIntentId: payload?.checkoutIntentId,
    }),
  );
  return {
    eventName: externalEventName,
    eventType,
    eventPayload: convertedPayload,
  };
}
